/**
 * Pure helpers for converting raw `StreamEvent`s into the typed
 * `StreamFrame` discriminated union, plus a handful of stateless
 * predicates the hook uses to drive side effects.
 *
 * Every export here is pure: no React, no setState, no analytics.
 * That makes them trivially unit-testable and safe to call from the
 * reducer or the hook.
 */
import { StreamEvent } from '../services/ChatService';
import {
  ContinuationFrame,
  ErrorFrame,
  FinalFrame,
  FramePayload,
  IntermediateFrame,
  StreamFrame,
} from '../types/streamProtocol';

// ---------------------------------------------------------------------------
// Payload extraction
// ---------------------------------------------------------------------------

function extractPayload(data: any): FramePayload {
  return {
    parts_result: data.parts_result || [],
    parts_list: data.parts_list ?? null,
    context_tags: data.context_tags ?? null,
    annotated_images: data.annotated_images ?? null,
    repair_instructions: data.repair_instructions ?? null,
    search_summarization: data.search_summarization ?? null,
    callToAction: data.callToAction ?? null,
    cta_actions: data.cta_actions ?? null,
    not_found_parts: data.not_found_parts ?? null,
    // Was never extracted, so the reason chips could not render on a live turn.
    not_found_reasons: data.not_found_reasons ?? null,
    error_code: data.error_code ?? null,
    requested_parts: data.requested_parts ?? null,
    lines: data.lines ?? null,
    turn_lines: data.turn_lines ?? null,
    ext_search_results: data.ext_search_results ?? null,
    vision_part_images: data.vision_part_images ?? null,
    transient: data.transient === true,
  };
}

/**
 * Parse the loose wire-shape `StreamEvent` into the typed `StreamFrame`
 * discriminated union.
 *
 * Resolution order:
 *   1. type === 'error'                       → ErrorFrame
 *   2. is_final && has_continuation           → ContinuationFrame
 *   3. is_final && !has_continuation          → FinalFrame
 *   4. otherwise                              → IntermediateFrame
 */
export function parseStreamEvent(event: StreamEvent): StreamFrame {
  if (event.type === 'quota_exceeded') {
    const d: any = event.data || {};
    return {
      kind: 'quota',
      metric: d.metric || 'llm_queries',
      resetsAt: d.resets_at || '',
      raw: event,
    };
  }

  if (event.type === 'error') {
    const msg =
      (event as any).error_details?.message ||
      event.data?.content ||
      'Unknown error';
    const details = (event as any).error_details || (event.data as any)?.error_details;
    const code = details?.code || (event.data as any)?.error_type || undefined;
    const retryableRaw = details?.retryable ?? (event.data as any)?.retryable;
    return {
      kind: 'error',
      message: msg,
      details,
      code,
      retryable: typeof retryableRaw === 'boolean' ? retryableRaw : undefined,
    };
  }

  const data = event.data || ({} as any);
  const base = {
    eventType: data.event_type || 'unknown',
    content: data.content || '',
    timestamp: data.timestamp,
    payload: extractPayload(data),
    raw: event,
  };

  if (data.event_type === 'complementary_update') {
    return { kind: 'suggestions', ...base };
  }

  if (data.is_final && data.has_continuation) {
    const cont: ContinuationFrame = {
      kind: 'continuation',
      hasError: detectFrameError(data),
      ...base,
    };
    return cont;
  }

  if (data.is_final) {
    const fin: FinalFrame = {
      kind: 'final',
      hasError: detectFrameError(data),
      ...base,
    };
    return fin;
  }

  const inter: IntermediateFrame = { kind: 'intermediate', ...base };
  return inter;
}

// ---------------------------------------------------------------------------
// Stateless predicates / extractors used by the hook
// ---------------------------------------------------------------------------

/** True when the SSE event signals a backend error, either via flag or content JSON. */
export function detectFrameError(data: any): boolean {
  if (data?.has_error === true) return true;
  try {
    const parsed = JSON.parse(data?.content || '{}');
    return parsed?.hasError === true;
  } catch {
    return false;
  }
}

/** Detects the silent ORDER_CREATED continuation marker emitted by the order workflow. */
export function detectOrderCreated(frame: ContinuationFrame): {
  isOrderCreated: boolean;
  orderId: number | null;
} {
  try {
    const parsed = JSON.parse(frame.content || '{}');
    const raw =
      typeof parsed?.text_message === 'string' ? parsed.text_message : frame.content;
    if (typeof raw === 'string' && raw.includes('ORDER_CREATED') && raw.includes('class="action"')) {
      const match = raw.match(/data-orderid="(\d+)"/);
      return { isOrderCreated: true, orderId: match ? parseInt(match[1], 10) : null };
    }
  } catch {
    /* not JSON */
  }
  return { isOrderCreated: false, orderId: null };
}

/** True when the message body is exclusively a silent ORDER_CREATED marker. */
export function isOrderCreatedOnly(text: string | undefined | null): boolean {
  if (!text) return false;
  let raw: string = text;
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed.text_message === 'string') {
      raw = parsed.text_message;
    }
  } catch {
    /* not JSON — use raw text */
  }
  return raw.includes('ORDER_CREATED') && raw.includes('class="action"');
}

/** Extract backend context tags from a final frame (or null if none). */
export function extractBackendTags(frame: FinalFrame | ContinuationFrame): string[] | null {
  const tags = frame.payload.context_tags;
  return Array.isArray(tags) && tags.length > 0 ? tags : null;
}
